import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import config from "../config";
import WorkerSidebarShell from "./WorkerSidebarShell";

const COUNTRY_CONFIG = {
  Uganda: { code: "+256", min: 9, max: 9 },
  Kenya: { code: "+254", min: 9, max: 9 },
  USA: { code: "+1", min: 10, max: 10 },
  UK: { code: "+44", min: 10, max: 10 },
  Canada: { code: "+1", min: 10, max: 10 },
  India: { code: "+91", min: 10, max: 10 }
};

const RemoteProfileEdit = () => {
  const navigate = useNavigate();
  const workerID = localStorage.getItem("workerID");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [spokenLanguage, setSpokenLanguage] = useState("");
  const [gender, setGender] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [rate, setRate] = useState(5);
  const [status, setStatus] = useState("available");
  
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [errors, setErrors] = useState({});
  const [modalConfig, setModalConfig] = useState(null);

  useEffect(() => {
    if (!workerID) {
      setLoadError("Session expired. Please log in again.");
      setLoading(false);
      return;
    }

    axios
      .get(`${config.API_BASE_URL}${config.WORKER.PROFILE}/${workerID}`)
      .then((res) => {
        const w = res.data || {};
        setFirstName(w.firstName || "");
        setLastName(w.lastName || "");
        setSpokenLanguage(w.spokenLanguage || "");
        setGender(w.gender || "");
        setCountry(w.country || "");
        setCity(w.city || "");
        setRate(w.rate || 5);
        setStatus(w.status || "available");

        if (w.country && COUNTRY_CONFIG[w.country]) {
          const cfg = COUNTRY_CONFIG[w.country];
          setCountryCode(cfg.code);
          if (w.whatsapp) {
            setWhatsapp(w.whatsapp.replace(cfg.code, ""));
          }
        }
      })
      .catch((err) => {
        if (err.response?.status !== 404) {
          setLoadError("Failed to load profile. You can still fill and save.");
        }
      })
      .finally(() => setLoading(false));
  }, [workerID]);

  const handleCountryChange = (e) => {
    const selected = e.target.value;
    setCountry(selected);
    setWhatsapp("");
    setCountryCode(COUNTRY_CONFIG[selected]?.code || "");
    setErrors({ ...errors, country: false });
  };

  const handleWhatsappChange = (e) => {
    setWhatsapp(e.target.value.replace(/\D/g, ""));
    setErrors({ ...errors, whatsapp: false });
  };

  const validateProfile = () => {
    const newErrors = {};

    if (!firstName) newErrors.firstName = true;
    if (!lastName) newErrors.lastName = true;
    if (!spokenLanguage) newErrors.spokenLanguage = true;
    if (!gender) newErrors.gender = true;
    if (!country) newErrors.country = true;
    if (!city) newErrors.city = true;
    if (!countryCode) newErrors.countryCode = true;
    if (!whatsapp) newErrors.whatsapp = true;
    if (!rate) newErrors.rate = true;

    const cfg = COUNTRY_CONFIG[country];
    if (country && !cfg) {
      newErrors.country = true;
    }

    if (country && cfg && (whatsapp.length < cfg.min || whatsapp.length > cfg.max)) {
      newErrors.whatsapp = true;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return false;
    }

    return true;
  };

  const saveProfile = async () => {
    if (!validateProfile()) return;

    try {
      await axios.post(`${config.API_BASE_URL}${config.WORKER.PROFILE}`, {
        workerID,
        firstName,
        lastName,
        spokenLanguage,
        gender,
        country,
        city,
        whatsapp: `${countryCode}${whatsapp}`,
        rate
      });
      setModalConfig({
        type: 'success',
        title: 'Profile Saved',
        message: 'Your profile has been updated successfully.'
      });
    } catch (err) {
      setModalConfig({
        type: 'error',
        title: 'Update Failed',
        message: err.response?.data?.error || "Failed to save profile."
      });
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      await axios.post(`${config.API_BASE_URL}/worker/status`, {
        workerID,
        status: newStatus
      });
      localStorage.setItem("workerStatus", newStatus);
    } catch {
      setModalConfig({
        type: 'error',
        title: 'Update Failed',
        message: 'Failed to update your status. Please try again later.'
      });
      // Revert state if failed
      setStatus(newStatus === "available" ? "unavailable" : "available");
    }
  };

  const toggleAvailability = () => {
    const newStatus = status === "available" ? "unavailable" : "available";
    setStatus(newStatus);
    updateStatus(newStatus);
  };

  if (loading) return <p style={{ padding: 40 }}>Loading profile...</p>;

  return (
    <WorkerSidebarShell activeItem="profile">
      <div style={card}>
        <div style={headerActionRow}>
          <h2 style={title}>Edit Worker Profile</h2>
          <button style={secondaryBtn} onClick={() => navigate("/remoteProfileViewer")}>
            View Profile
          </button>
        </div>

        {loadError && <p style={warningText}>{loadError}</p>}

        <div style={gridRow}>
          <Input 
            label="First Name" 
            value={firstName} 
            setValue={(val) => { setFirstName(val); setErrors({ ...errors, firstName: false }); }} 
            error={errors.firstName} 
          />
          <Input 
            label="Last Name" 
            value={lastName} 
            setValue={(val) => { setLastName(val); setErrors({ ...errors, lastName: false }); }} 
            error={errors.lastName} 
          />
        </div>

        <div style={gridRow}>
          <Input 
            label="Spoken Language" 
            value={spokenLanguage} 
            setValue={(val) => { setSpokenLanguage(val); setErrors({ ...errors, spokenLanguage: false }); }} 
            error={errors.spokenLanguage} 
          />
          <Select
            label="Gender"
            value={gender}
            onChange={(e) => { setGender(e.target.value); setErrors({ ...errors, gender: false }); }}
            error={errors.gender}
            options={[
              { label: "Select Gender", value: "" },
              { label: "Male", value: "M" },
              { label: "Female", value: "F" }
            ]}
          />
        </div>

        <div style={gridRow}>
          <Select
            label="Country"
            value={country}
            onChange={handleCountryChange}
            error={errors.country}
            options={[
              { label: "Select Country", value: "" },
              ...Object.keys(COUNTRY_CONFIG).map((c) => ({ label: c, value: c }))
            ]}
          />
          <Input 
            label="City" 
            value={city} 
            setValue={(val) => { setCity(val); setErrors({ ...errors, city: false }); }} 
            error={errors.city} 
          />
        </div>

        <div style={inputGroup}>
          <label style={{ ...labelStyle, color: errors.whatsapp ? '#dc2626' : labelStyle.color }}>WhatsApp Number</label>
          <div style={phoneRow}>
            <input style={{ ...codeInput, borderColor: errors.countryCode ? '#dc2626' : codeInput.borderColor }} value={countryCode} disabled />
            <input style={{ ...phoneInput, borderColor: errors.whatsapp ? '#dc2626' : phoneInput.borderColor }} value={whatsapp} onChange={handleWhatsappChange} />
          </div>
          {errors.whatsapp && <span style={errorHint}>Please enter a valid phone number.</span>}
        </div>

        <div style={gridRow}>
          <Select
            label="Hourly Rate"
            value={rate}
            onChange={(e) => { setRate(Number(e.target.value)); setErrors({ ...errors, rate: false }); }}
            error={errors.rate}
            options={[...Array(20)].map((_, i) => ({
              label: `$${i + 1}/hr`,
              value: i + 1
            }))}
          />
          
          <div style={inputGroup}>
            <label style={labelStyle}>Availability Status</label>
            <div 
              style={toggleContainer} 
              onClick={toggleAvailability}
            >
              <div style={status === 'available' ? toggleTrackActive : toggleTrack}>
                <div style={status === 'available' ? toggleThumbActive : toggleThumb} />
              </div>
              <span style={status === 'available' ? toggleLabelActive : toggleLabel}>
                {status === 'available' ? "Available" : "Not Available"}
              </span>
            </div>
          </div>
        </div>

        <div style={actionRow}>
          <button style={secondaryBtn} onClick={() => navigate("/worker-home")}>
            Back to Dashboard
          </button>
          <button style={primaryBtn} onClick={saveProfile}>
            Save Profile
          </button>
        </div>
      </div>

      {/* ======================
          MODAL
      ====================== */}
      {modalConfig && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={modalConfig.type === 'error' ? errorIconWrapper : successIconWrapper}>
              {modalConfig.type === 'error' ? (
                <FaExclamationCircle style={modalIconError} />
              ) : (
                <FaCheckCircle style={modalIconSuccess} />
              )}
            </div>
            <h3 style={modalTitle}>{modalConfig.title}</h3>
            <p style={modalText}>{modalConfig.message}</p>
            <button
              style={modalConfig.type === 'error' ? confirmModalBtnError : confirmModalBtnSuccess}
              onClick={() => setModalConfig(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </WorkerSidebarShell>
  );
};

export default RemoteProfileEdit;

const Input = ({ label, value, setValue, error }) => (
  <div style={inputGroup}>
    <label style={{ ...labelStyle, color: error ? '#dc2626' : labelStyle.color }}>{label}</label>
    <input 
      style={{ ...input, borderColor: error ? '#dc2626' : input.borderColor }} 
      value={value} 
      onChange={(e) => setValue(e.target.value)} 
    />
    {error && <span style={errorHint}>This field is required.</span>}
  </div>
);

const Select = ({ label, value, onChange, options, error }) => (
  <div style={inputGroup}>
    <label style={{ ...labelStyle, color: error ? '#dc2626' : labelStyle.color }}>{label}</label>
    <select 
      style={{ ...input, borderColor: error ? '#dc2626' : input.borderColor }} 
      value={value} 
      onChange={onChange}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    {error && <span style={errorHint}>Please select an option.</span>}
  </div>
);

const card = {
  background: "#fff",
  padding: "32px",
  width: "100%",
  maxWidth: "900px",
  borderRadius: "12px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
  margin: "0 auto",
  boxSizing: "border-box"
};

const headerActionRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "20px",
  borderBottom: "1px solid #edf2f7",
  paddingBottom: "16px"
};

const title = {
  color: "#0E5580",
  margin: 0,
  fontSize: "24px",
  fontWeight: 600
};

const gridRow = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: "12px"
};

const inputGroup = {
  display: "flex",
  flexDirection: "column",
  marginBottom: "14px"
};

const labelStyle = {
  fontSize: "13px",
  marginBottom: "4px",
  color: "#4f6475",
  fontWeight: 600
};

const input = {
  padding: "10px",
  borderRadius: "8px",
  border: "1px solid #d6e3ef",
  fontSize: "14px",
  color: "#234159",
  background: "#fff"
};

const phoneRow = {
  display: "flex",
  gap: "6px"
};

const codeInput = {
  width: "80px",
  padding: "10px",
  borderRadius: "8px",
  border: "1px solid #d6e3ef",
  background: "#f0f5fa"
};

const phoneInput = {
  flex: 1,
  padding: "10px",
  borderRadius: "8px",
  border: "1px solid #d6e3ef"
};

const actionRow = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
  marginTop: "20px",
  borderTop: "1px solid #edf2f7",
  paddingTop: "16px",
  flexWrap: "wrap"
};

const primaryBtn = {
  padding: "10px 18px",
  background: "#0E5580",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer"
};

const secondaryBtn = {
  padding: "10px 18px",
  background: "#fff",
  color: "#0E5580",
  border: "1px solid #0E5580",
  borderRadius: "8px",
  cursor: "pointer"
};

const warningText = {
  background: "#fff3cd",
  color: "#664d03",
  border: "1px solid #ffe69c",
  borderRadius: "8px",
  padding: "10px",
  marginBottom: "12px",
  fontSize: "13px"
};

const errorHint = {
  color: "#dc2626",
  fontSize: "12px",
  marginTop: "4px"
};

const toggleContainer = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  cursor: "pointer",
  padding: "4px 0",
  marginTop: "4px"
};

const toggleTrack = {
  width: "44px",
  height: "24px",
  background: "#cbd5e1",
  borderRadius: "12px",
  position: "relative",
  transition: "background 0.3s ease"
};

const toggleTrackActive = {
  ...toggleTrack,
  background: "#10b981" // green
};

const toggleThumb = {
  width: "18px",
  height: "18px",
  background: "#fff",
  borderRadius: "50%",
  position: "absolute",
  top: "3px",
  left: "3px",
  transition: "transform 0.3s ease",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
};

const toggleThumbActive = {
  ...toggleThumb,
  transform: "translateX(20px)"
};

const toggleLabel = {
  fontSize: "14px",
  fontWeight: "500",
  color: "#64748b"
};

const toggleLabelActive = {
  fontSize: "14px",
  fontWeight: "600",
  color: "#10b981"
};

// Modal Styles
const modalOverlay = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(15, 23, 42, 0.6)",
  backdropFilter: "blur(4px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  animation: "fadeIn 0.2s ease-out"
};

const modalContent = {
  background: "#fff",
  borderRadius: "20px",
  padding: "32px",
  width: "90%",
  maxWidth: "400px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  animation: "slideUp 0.3s ease-out",
  textAlign: "center"
};

const errorIconWrapper = {
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  background: "#fef2f2",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px auto"
};

const successIconWrapper = {
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  background: "#f0fdf4",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px auto"
};

const modalIconError = {
  fontSize: "24px",
  color: "#ef4444"
};

const modalIconSuccess = {
  fontSize: "24px",
  color: "#10b981"
};

const modalTitle = {
  fontSize: "20px",
  fontWeight: "700",
  color: "#0f172a",
  margin: "0 0 12px 0"
};

const modalText = {
  fontSize: "15px",
  color: "#64748b",
  lineHeight: "1.5",
  margin: "0 0 24px 0"
};

const confirmModalBtnError = {
  width: "100%",
  padding: "12px",
  background: "#ef4444",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  fontSize: "15px",
  fontWeight: "600",
  cursor: "pointer",
  transition: "background 0.2s"
};

const confirmModalBtnSuccess = {
  width: "100%",
  padding: "12px",
  background: "#10b981",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  fontSize: "15px",
  fontWeight: "600",
  cursor: "pointer",
  transition: "background 0.2s"
};
